import React from 'react';

import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { IconButton, LinkIconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Image from '@atlaskit/image';
import { Box, Flex, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import type { cardHeader_updateWorkItemFieldsActionRenderer$key } from './__generated__/cardHeader_updateWorkItemFieldsActionRenderer.graphql';

const messages = defineMessages({
	openInANewTab: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.card-header.openInANewTab',
		defaultMessage: 'Open in a new tab',
		description: 'Label for the open in a new tab button',
	},
	expandSuggestions: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.card-header.expandSuggestions',
		defaultMessage: 'Expand suggestions',
		description: 'Label for the expand suggestions button',
	},
	collapseSuggestions: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.card-header.collapseSuggestions',
		defaultMessage: 'Collapse suggestions',
		description: 'Label for the collapse suggestions button',
	},
});

// DO NOT CHANGE THE VALUE OF THIS VARIABLE WITHOUT CHANGING THE CSS SELECTORS.
// const test-id as we are selecting icon buttons container using CSS selectors to show the icon buttons when the header is hovered or focused within.
// As cssMap only supports static strings, we cannot use this variable in the CSS selectors.
export const ICON_BUTTONS_CONTAINER_TEST_ID =
	'update-work-item-fields-action-renderer.ui.action-card.card-header.icon-buttons-container';

const styles = cssMap({
	root: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),

		// DO NOT CHANGE THE DATA-TESTID SELECTORS WITHOUT CHANGING ICON_BUTTONS_CONTAINER_TEST_ID.
		// We are using CSS selectors here to show the icon buttons when the header is hovered or focused within. As the Rovo chat UI is subject to layout shifts
		// when we expand/collapse suggestions, we use CSS selectors instead of Javascript to be more robust and accurate on states where the icon buttons are hidden/shown.
		// For futher context: https://atlassian.slack.com/archives/C092WQZSBSP/p1763093753796659
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-testid="update-work-item-fields-action-renderer.ui.action-card.card-header.icon-buttons-container"]':
			{
				// 0px to allow text to take full width of the card header when the buttons are hidden.
				width: '0px',
				opacity: 0,
				overflow: 'hidden',
			},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover [data-testid="update-work-item-fields-action-renderer.ui.action-card.card-header.icon-buttons-container"], &:focus-within [data-testid="update-work-item-fields-action-renderer.ui.action-card.card-header.icon-buttons-container"]':
			{
				width: 'auto',
				opacity: 1,
				overflow: 'visible',
			},
	},
	avatar: {
		marginTop: token('space.050'),
		marginRight: token('space.050'),
		marginBottom: token('space.050'),
		marginLeft: token('space.050'),
		minWidth: 'fit-content',
	},
	workItemKeyContainer: {
		minWidth: 'fit-content',
	},
	pressable: {
		display: 'flex',
		backgroundColor: token('color.background.neutral.subtle'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		borderRadius: token('radius.small'),
		textAlign: 'left',
		// flex-grow to 1 such that the pressable fills the remainder of the header
		flexGrow: 1,
	},
});

export type Props = {
	queryRef: cardHeader_updateWorkItemFieldsActionRenderer$key;
	onClick?: () => void;
	isExpanded?: boolean;
	showButtons?: boolean;
};

export const CardHeader = ({
	queryRef,
	isExpanded,
	onClick,
	showButtons = true,
}: Props): React.JSX.Element | null => {
	di(Tooltip);

	const { formatMessage } = useIntl();

	const issue = useFragment(
		graphql`
			fragment cardHeader_updateWorkItemFieldsActionRenderer on JiraIssue {
				key
				summaryField {
					text
				}
				issueTypeField {
					issueType {
						name
						avatar {
							small
						}
					}
				}
				webUrl
			}
		`,
		queryRef,
	);

	if (!issue) {
		return null;
	}

	const issueType = issue?.issueTypeField?.issueType;
	const issueUrl = issue?.webUrl ?? '';

	return (
		<Inline
			space="space.100"
			alignBlock="center"
			spread="space-between"
			xcss={styles.root}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-header"
		>
			{/* Pressable has a tabIndex of -1 such that it is not reachable via Tabbing throughout the page. This is as
			clicking the Pressable and clicking expand/collapse button have the same behaviour of expanding/collapsing the
			suggestions. To reduce confusion and duplication of functionality for keyboard users, the Pressable is hence given
			the negative tabIndex, and can only expand/collapse the suggestions via the expand/collapse button.
			For keyboard and mouse users, they can still click both the Pressable and the expand/collapse button to expand/collapse the suggestions.
			*/}
			<Pressable
				xcss={styles.pressable}
				onClick={onClick}
				tabIndex={-1}
				onMouseDown={(e) => e.preventDefault()}
			>
				<Tooltip position="bottom" content={`${issue.key}: ${issue?.summaryField?.text}`}>
					<Inline space="space.050" alignBlock="center" alignInline="center">
						<Flex xcss={styles.avatar}>
							<Image
								alt={issueType?.name}
								src={issueType?.avatar?.small ?? undefined}
								width={16}
								height={16}
							/>
						</Flex>
						<Box xcss={styles.workItemKeyContainer}>
							<Text color="color.text.subtle">{issue?.key}</Text>
						</Box>
						<Text as="strong" maxLines={1}>
							{issue?.summaryField?.text}
						</Text>
					</Inline>
				</Tooltip>
			</Pressable>
			{showButtons && (
				<Inline space="space.050" alignBlock="center" alignInline="center">
					{/* We only want to show the open in new tab link button on hover/focus
				https://atlassian.slack.com/archives/C094SSLU8A0/p1764915281282169?thread_ts=1764897370.301789&cid=C094SSLU8A0
				*/}
					<Box testId={ICON_BUTTONS_CONTAINER_TEST_ID}>
						{/* We are not using the built-in prop of isTooltipDisabled={false} as for LinkIconButton, there is a bug where it overrides the compact spacing.
				https://atlassian.slack.com/archives/CFJ9DU39U/p1762996271131979
				TODO: Use built-in prop and remove Tooltip wrapper when bug is resolved.
				*/}
						<Tooltip
							position="bottom"
							content={formatMessage(messages.openInANewTab)}
							// As we are not using the inbuilt prop, the content will be read twice if we don't include this.
							isScreenReaderAnnouncementDisabled
						>
							{(tooltipProps) => (
								<LinkIconButton
									{...tooltipProps}
									icon={LinkExternalIcon}
									label={formatMessage(messages.openInANewTab)}
									href={issueUrl}
									target="_blank"
									appearance="subtle"
									spacing="compact"
									testId="update-work-item-fields-action-renderer.ui.action-card.card-header.external-link-button"
									// Prevent default onMouseDown behaviour so that the button doesn't remained focused after we click on it. This is so that
									// if a user hovers off the header after clicking these buttons, the buttons are not visible - before, they would remain
									// visible as they were still in focus.
									onMouseDown={(e) => e.preventDefault()}
								/>
							)}
						</Tooltip>
					</Box>

					<IconButton
						icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
						label={
							isExpanded
								? formatMessage(messages.collapseSuggestions)
								: formatMessage(messages.expandSuggestions)
						}
						onClick={onClick}
						appearance="subtle"
						spacing="compact"
						testId="update-work-item-fields-action-renderer.ui.action-card.card-header.expand-collapse-button"
						isTooltipDisabled={false}
						// Prevent default onMouseDown behaviour so that the button doesn't remained focused after we click on it. This is so that
						// if a user hovers off the header after clicking these buttons, the buttons are not visible - before, they would remain
						// visible as they were still in focus.
						onMouseDown={(e) => e.preventDefault()}
					/>
				</Inline>
			)}
		</Inline>
	);
};
